import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionHead } from "@/pages/Home";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import {
  ArrowRight, Search, PenTool, BarChart3, Workflow, Compass, Bot,
} from "lucide-react";

const PHASES = [
  { key: "plan", label: "Plan", desc: "Clarify your mission and map the right approach." },
  { key: "launch", label: "Launch", desc: "Get a credible, original website live." },
  { key: "manage", label: "Manage", desc: "Keep it secure, current, and cared for." },
  { key: "grow", label: "Grow", desc: "Reach more of the people you serve.", active: true },
];

const GROW_AREAS = [
  { icon: Search, title: "Get found (SEO)", desc: "Help the people who need your mission actually find you in search." },
  { icon: PenTool, title: "Fresh content", desc: "Pages, posts, and appeals that speak to your audience—kept up to date." },
  { icon: BarChart3, title: "Plain-English insight", desc: "Understand what's working, without drowning in dashboards." },
  { icon: Workflow, title: "Helpful automation", desc: "Let routine follow-ups and updates happen quietly in the background." },
  { icon: Compass, title: "Monthly guidance", desc: "A clear, small set of next steps each month—progress, not overwhelm." },
];

export default function Grow() {
  return (
    <div data-testid="grow-page">
      {/* HERO */}
      <section className="relative py-28 md:py-36 border-b border-white/5 grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-6">
              <span className="h-px w-10 bg-gold" /> Plan · Launch · Manage · Grow
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white leading-[1.08]">
              Growing your mission doesn't stop at <span className="text-gold">launch</span>.
            </h1>
            <p className="text-lg text-gray-300 font-light mt-8 leading-relaxed">
              A website going live is a beginning, not a finish line. Once you're launched, we help you
              reach more of the people you serve—and keep making steady, meaningful progress.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PHASE STRIP */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="grow-phase-strip">
          {PHASES.map((p, i) => (
            <div
              key={p.key}
              className={`relative border p-6 transition-colors ${p.active ? "border-gold/60 bg-navy-800" : "border-white/10 bg-navy-800/40"}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className={`font-mono text-sm ${p.active ? "text-gold" : "text-white/25"}`}>0{i + 1}</span>
                <h3 className={`font-heading text-xl ${p.active ? "text-gold" : "text-white"}`}>{p.label}</h3>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              {p.active && <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold to-transparent" />}
            </div>
          ))}
        </div>
      </section>

      {/* GROW AREAS */}
      <section className="py-16 md:py-24 bg-navy-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead label="The Grow phase" title="Ways we help you keep moving forward" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14" data-testid="grow-areas">
            {GROW_AREAS.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="group glass p-8 h-full hover:border-gold/40 transition-colors"
              >
                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold mb-6 group-hover:scale-110 transition-transform duration-300">
                  <a.icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-white mb-3 leading-snug">{a.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AIPOS — one of the tools */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 flex lg:justify-center">
            <div className="w-16 h-16 border border-gold/40 flex items-center justify-center text-gold">
              <Bot size={28} strokeWidth={1.5} />
            </div>
          </div>
          <div className="lg:col-span-10">
            <SectionHead label="One of the tools that helps" title="Where Top Foster fits in" />
            <p className="text-gray-300 text-lg font-light leading-relaxed max-w-3xl mt-6" data-testid="grow-aipos-note">
              <GlossaryTerm term="Top Foster" className="text-white">Top Foster</GlossaryTerm> is our AI that can draft content, suggest SEO improvements, and point you
              toward sensible next steps. It's a helpful tool along the way—but it's not the whole story.
              The heart of the Grow phase is a real partnership: technology in service of your people and
              your mission, with a team that stays in your corner.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden bg-gradient-to-br from-navy-700 to-navy-800 border border-gold/20 p-12 md:p-16 text-center grain">
          <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-4">Wherever you are, we can help you grow.</h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-9">Not launched yet? Let's plan it. Already live? Let's help more people find you.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/start" data-testid="grow-cta-start" className="inline-flex items-center gap-2 bg-gold text-navy px-9 py-4 font-semibold hover:bg-gold-light transition-colors">
              Start with a plan <ArrowRight size={18} />
            </Link>
            <Link to="/marketplace" data-testid="grow-cta-marketplace" className="inline-flex items-center gap-2 border border-white/25 text-white px-9 py-4 font-medium hover:bg-white/5 transition-colors">
              See our work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
